import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n';
import { Pill } from '../../ui/DataBits';
import { Markdown } from '../../ui/Markdown';
import { hasLang, loc, type Loc } from '../../lib/loc';
import { errorText, fmtDate, rpc, type AppError } from '../admin/api';

export type ComplianceDoc = {
  key: string;
  version: number;
  title: Loc;
  body: Loc;
  audience?: string;
  is_public: boolean;
  requires_acceptance?: boolean;
  is_draft: boolean;
  blocked_reason?: string | null;
  published_at: string;
  change_note?: string | null;
  accepted?: { version: number; on: string; signatory: string } | null;
};

// J2: the compliance pack as a school sees it. Each document is versioned;
// documents the school signs show which version it last accepted.
export function ComplianceList({ source = 'school_compliance' }: { source?: 'school_compliance' | 'public_compliance_documents' }) {
  const { t, lang, locale } = useI18n();
  const [docs, setDocs] = useState<ComplianceDoc[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  useEffect(() => {
    rpc<ComplianceDoc[]>(source).then((r) => (r.error ? setErr(r.error) : setDocs(r.data)));
  }, [source]);
  if (err) return <p className="notice notice-block" role="alert">{errorText(t, err)}</p>;
  if (!docs) return <p role="status">{t.loading}</p>;
  return (
    <>
      <p className="notice notice-block">{source === 'school_compliance' ? t['cp.intro'] : t['cp.publicIntro']}</p>
      <ul className="doc-list">
        {docs.map((d) => {
          const id = `doc-${d.key}`;
          const shown = open === d.key;
          return (
            <li key={d.key} className="panel p5">
              <div className="panel-head">
                <h2 className="panel-title" id={id}>{loc(d.title, lang)}</h2>
                <span className="doc-pills">
                  {d.is_draft && <Pill kind="warn">{t['ts.draft']}</Pill>}
                  {d.blocked_reason && <Pill kind="crit">{t['cp.blocked']}</Pill>}
                  {d.requires_acceptance && (d.accepted
                    ? <Pill kind={d.accepted.version === d.version ? 'good' : 'warn'}>{t['cp.accepted'](d.accepted.version)}</Pill>
                    : <Pill kind="neutral">{t['cp.notAccepted']}</Pill>)}
                </span>
              </div>
              <p className="text-muted">
                {t['cp.version'](d.version, fmtDate(locale, d.published_at))}
                {d.audience && <> · {t['cp.for'](d.audience)}</>}
                {d.is_public && <> · {t['cp.public']}</>}
              </p>
              {d.blocked_reason && <p className="form-hint">{d.blocked_reason}</p>}
              {d.accepted && <p className="form-hint">{t['cp.acceptedBy'](d.accepted.signatory, fmtDate(locale, d.accepted.on))}</p>}
              <button type="button" className="link-btn" aria-expanded={shown} aria-controls={`${id}-body`} onClick={() => setOpen(shown ? null : d.key)}>
                {shown ? t['cp.hide'] : t['cp.read']}
              </button>
              {shown && (
                <div id={`${id}-body`} className="doc-body">
                  {!hasLang(d.body, lang) && <p className="notice notice-block">{t['cp.onlyEnglish']}</p>}
                  <div lang={hasLang(d.body, lang) ? lang : 'en'}>
                    <Markdown text={loc(d.body, lang)} />
                  </div>
                  <button type="button" className="btn btn-ghost" onClick={() => download(d, lang)}>{t['cp.download']}</button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function download(d: ComplianceDoc, lang: string) {
  const blob = new Blob([loc(d.body, lang)], { type: 'text/markdown' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${d.key}-v${d.version}-${hasLang(d.body, lang) ? lang : 'en'}.md`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
